/* eslint-disable max-len */
import { useApolloClient } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useModal } from "../context/ModalContext";
import { usePetStore } from "./redux-hooks/pet-store/pet-store.service";
import {
  MODALS,
  UPSELL_RENEWAL_ENTRY_POINT_KEY,
  type WellnessRenewalEntryPoint,
} from "../config/params";
import { PAGES_PATH } from "../navigation/params";
import { ICommonWPClass } from "../services/get-alerts/get-alerts.interface";
import {
  GET_PREQUALIFICATION_INFO,
  hasValidPrequalification,
  IPrequalificationInfoResponse,
} from "../services/prequalification";
import { userActivityLogger } from "../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../config/mixPanelEvents";

/**
 * Custom hook to handle wellness plan renewal funnel navigation
 * Supports both single and multiple pet renewal flows
 * Handles birthday validation and navigation to appropriate screens
 *
 * Prequalification gate (SGPD): before running the classic funnel it checks
 * the customer's latest prequalification. A valid (non-expired, unfulfilled,
 * ELIGIBLE/INELIGIBLE) record lets the flow continue straight to plan/pet
 * selection; otherwise the interceptor modal ("A New Way to Pay") is shown and
 * the customer is routed through the /prequalification funnel instead.
 * @return {object} Object containing startRenewalFunnel function
 */
export const useWellnessRenewalFunnel = () => {
  const { petList } = usePetStore();
  const { openModal, setWellnessSelectedPetId } = useModal();
  const client = useApolloClient();
  const navigate = useNavigate();

  /**
   * Existing renewal routing (multi-pet selector or single-pet plan details).
   * Runs once the prequalification gate has been satisfied.
   * @param {ICommonWPClass[]} renewalData Pets needing renewal.
   * @param {WellnessRenewalEntryPoint} source Analytics source identifier.
   * @return {void}
   */
  const proceedWithRenewalFunnel = (
    renewalData: ICommonWPClass[],
    source: WellnessRenewalEntryPoint,
  ) => {
    if (renewalData.length > 1) {
      // Multiple pets - show pet selector with filtered pet IDs
      openModal(MODALS.WELLNESS_PLAN_SELECT_PET, source, {
        petIds: renewalData.map((p) => p.petId),
      });
    } else {
      // Single pet - navigate directly
      const petId = renewalData[0].petId;

      setWellnessSelectedPetId(petId);

      const pet = petList?.find((p) => p.id === petId);
      const isNotValidBirthdate =
        !pet?.birthdate ||
        pet.birthdate.length === 0 ||
        parseInt(pet.birthdate) <= 0;

      if (isNotValidBirthdate) {
        // Need birthday first, then will go to plan comparison
        openModal(MODALS.PET_BIRTHDAY_FORM_MODAL);
      } else {
        // Ready for plan comparison
        navigate(`${PAGES_PATH.WELLNESS_PLAN_DETAILS}?id=${petId}`);
      }
    }
  };

  /**
   * Navigate user through renewal funnel based on renewal data
   * @param {ICommonWPClass[]} renewalData - Array of pets needing renewal
   * @param {WellnessRenewalEntryPoint} source - Source identifier for analytics
   * @return {Promise<void>}
   */
  const startRenewalFunnel = async (
    renewalData: ICommonWPClass[],
    source: WellnessRenewalEntryPoint,
  ) => {
    if (source) {
      localStorage.setItem(UPSELL_RENEWAL_ENTRY_POINT_KEY, source);
    }

    // Tracks when user starts the renewal funnel (from any entry point)
    userActivityLogger(MIXPANEL_EVENTS.INTEREST_IN_WELLNESS_PLAN, { referred_from: source });

    if (!renewalData || renewalData.length === 0) {
      openModal(MODALS.ADD_A_PET);

      return;
    }

    renewalData.forEach((petData) => {
      userActivityLogger(MIXPANEL_EVENTS.USER_CLICKS_REVIEW_NEW_TOTALCARE_PLANS, {
        is_auto_renewal_on: petData.isAutoRenewal,
        pet_name: petData.petName,
        plan_name: petData.wellnessPlanName,
        referred_from: source,
        pet_id: petData.petId,
      });
    });

    // Prequalification gate: valid record -> classic funnel; missing/invalid
    // record -> interceptor modal (routes into /prequalification). If the gate
    // query itself fails, fall back to the classic renewal funnel rather than
    // trapping the customer in the prequal path.
    //
    // NOTE: we use client.query() rather than a useLazyQuery execute fn on
    // purpose. On Apollo 3.6.x the lazy execute promise (a) never rejects on
    // error and (b) never settles if the owning component unmounts before it
    // resolves
    let hasValidPrequal = false;
    try {
      const { data } = await client.query<{
        getPrequalificationInfo: IPrequalificationInfoResponse;
      }>({
        query: GET_PREQUALIFICATION_INFO,
        fetchPolicy: "no-cache",
        context: { suppressGlobalErrorToast: true },
      });
      hasValidPrequal = hasValidPrequalification(data?.getPrequalificationInfo);
    } catch {
      hasValidPrequal = true;
    }

    if (!hasValidPrequal) {
      openModal(MODALS.PREQUAL_INTERCEPTOR_MODAL, source, {
        renewalPetIds: renewalData.map((p) => p.petId),
      });
      return;
    }

    proceedWithRenewalFunnel(renewalData, source);
  };

  return {
    startRenewalFunnel,
  };
};

